import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Plus, ExternalLink, Trash2, Edit3, FileText } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/sonner';
import { useBrief, useUpdateBriefSlice } from '@/lib/brief';
import { useSession } from '@/lib/auth';
import { cn } from '@/lib/cn';

/**
 * `/strony` — page library + CRUD.
 *
 * Two sections:
 *   1. Built-in pages (home, about, contact, opinie). Each has an "Edytuj"
 *      link to /strony/<id>.
 *   2. Custom pages (brief.custom_pages[]). User adds via the "Nowa strona"
 *      dialog — picks archetype from the wireframe library + sets path.
 *      Stored in brief.custom_pages so the existing brief PUT slice plumbing
 *      handles persistence.
 */

export const Route = createFileRoute('/_app/strony/')({
  staticData: { crumb: 'Strony' },
  component: StronyPage,
});

type PageArchetype =
  | 'home' | 'home-boutique' | 'home-editorial'
  | 'about' | 'contact' | 'reviews' | 'procedure'
  | 'article' | 'landing' | 'emergency' | 'booking';

interface CustomPage {
  id: string;
  path: string;
  archetype: PageArchetype;
  title: string;
  description?: string;
  created_at?: number;
  updated_at?: number;
}

const ARCHETYPES: { id: PageArchetype; name: string; description: string; icon: string }[] = [
  { id: 'home', name: 'Strona główna (Standard)', description: 'Hero + zaufanie + usługi + zespół + opinie + o nas + kontakt + CTA', icon: '🏠' },
  { id: 'home-boutique', name: 'Strona główna (Boutique)', description: 'Cinematic hero + filozofia + zabiegi premium', icon: '✨' },
  { id: 'home-editorial', name: 'Strona główna (Editorial)', description: 'Cytat-hero + story + numbers + galeria', icon: '📰' },
  { id: 'about', name: 'O nas', description: 'Story-block + zespół + akredytacje', icon: '👥' },
  { id: 'contact', name: 'Kontakt', description: 'Mapa + godziny + formularz + dane', icon: '📍' },
  { id: 'reviews', name: 'Opinie', description: 'Lista opinii + agregat oceny + filtry', icon: '⭐' },
  { id: 'procedure', name: 'Zabieg / Usługa', description: 'Procedure-meta + timeline + FAQ + before/after', icon: '🩺' },
  { id: 'article', name: 'Artykuł / Poradnik', description: 'Hero + body prose + related + FAQ', icon: '📝' },
  { id: 'landing', name: 'Landing kampanii', description: 'Hero + 3 punkty + dowód + CTA', icon: '🎯' },
  { id: 'emergency', name: 'Dyżur / Pomoc nagła', description: 'Hero 24h + telefon + lokalizacja + co robić', icon: '🚨' },
  { id: 'booking', name: 'Rezerwacja online', description: 'Hero + iframe rezerwacji + FAQ', icon: '📅' },
];

const BUILTIN_PAGES: Array<{ id: string; path: string; title: string; archetype: PageArchetype; description: string }> = [
  { id: 'builtin-home', path: '/', title: 'Strona główna', archetype: 'home', description: 'Wygenerowana automatycznie. Edytuj wygląd w „Wygląd strony".' },
  { id: 'builtin-about', path: '/o-nas', title: 'O nas', archetype: 'about', description: 'Story + zespół + filozofia kliniki.' },
  { id: 'builtin-contact', path: '/kontakt', title: 'Kontakt', archetype: 'contact', description: 'Mapa, godziny, formularz, telefon.' },
  { id: 'builtin-reviews', path: '/opinie', title: 'Opinie', archetype: 'reviews', description: 'Wszystkie opinie z agregatem oceny.' },
];

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

function StronyPage() {
  const { data: brief } = useBrief();
  const update = useUpdateBriefSlice();
  const { data: session } = useSession();
  const tenantSlug = session?.tenant?.slug;

  const customPages = React.useMemo(() => {
    const c = (brief as unknown as { custom_pages?: CustomPage[] })?.custom_pages ?? [];
    return Array.isArray(c) ? c : [];
  }, [brief]);

  const [newOpen, setNewOpen] = React.useState(false);
  const [deletePage, setDeletePage] = React.useState<CustomPage | null>(null);

  const addPage = async (page: CustomPage) => {
    const next = [...customPages, page];
    try {
      await update.mutateAsync({ slice: 'custom_pages', data: next });
      toast.success(`Strona „${page.title}" dodana`);
      setNewOpen(false);
    } catch {
      toast.error('Nie udało się dodać strony');
    }
  };

  const removePage = async (id: string) => {
    const next = customPages.filter((p) => p.id !== id);
    try {
      await update.mutateAsync({ slice: 'custom_pages', data: next });
      toast.success('Strona usunięta');
      setDeletePage(null);
    } catch {
      toast.error('Nie udało się usunąć strony');
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-text">Strony</h1>
          <p className="mt-1 text-sm text-text-muted">
            Każda strona ma własny edytor z podglądem na żywo. Możesz też dodać własne strony
            wybierając szablon z biblioteki wireframes.
          </p>
        </div>
        <Button type="button" onClick={() => setNewOpen(true)}>
          <Plus className="size-4" aria-hidden="true" /> Nowa strona
        </Button>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Strony wbudowane</CardTitle>
          <CardDescription>
            Domyślny zestaw — każda klinika je dostaje. Edycji podlega wygląd i sekcje opcjonalne.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul role="list" className="grid gap-3 md:grid-cols-2">
            {BUILTIN_PAGES.map((p) => (
              <PageRow
                key={p.id}
                page={{ ...p, builtin: true }}
                tenantSlug={tenantSlug}
              />
            ))}
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Strony własne <span className="ml-2 text-sm font-normal text-text-subtle tabular-nums">{customPages.length}</span>
          </CardTitle>
          <CardDescription>
            Dodaj artykuły, dedykowane landingi, strony kampanii. Każda korzysta z szablonu
            z biblioteki wireframes.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {customPages.length === 0 ? (
            <p className="rounded-md bg-surface p-4 text-sm text-text-muted ring-1 ring-black/5">
              Brak własnych stron. Kliknij <strong>Nowa strona</strong>, aby dodać.
            </p>
          ) : (
            <ul role="list" className="grid gap-3 md:grid-cols-2">
              {customPages.map((p) => (
                <PageRow
                  key={p.id}
                  page={{ ...p, builtin: false }}
                  tenantSlug={tenantSlug}
                  onDelete={() => setDeletePage(p)}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {newOpen ? (
        <NewPageDialog
          existingPaths={[
            ...BUILTIN_PAGES.map((p) => p.path),
            ...customPages.map((p) => p.path),
          ]}
          onCancel={() => setNewOpen(false)}
          onCreate={addPage}
        />
      ) : null}

      <AlertDialog open={!!deletePage} onOpenChange={(o) => !o && setDeletePage(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Usunąć stronę „{deletePage?.title}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Ścieżka <code className="rounded bg-surface px-1 font-mono text-xs">{deletePage?.path}</code> przestanie
              być serwowana po następnej publikacji. Tej operacji nie da się cofnąć z poziomu UI.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Anuluj</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deletePage && removePage(deletePage.id)}
              className="bg-error text-text-inverted hover:bg-error/90"
            >
              Usuń stronę
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function PageRow({
  page,
  tenantSlug,
  onDelete,
}: {
  page: { id: string; path: string; title: string; archetype: PageArchetype; description?: string; builtin: boolean };
  tenantSlug: string | undefined;
  onDelete?: () => void;
}) {
  const arch = ARCHETYPES.find((a) => a.id === page.archetype);
  const publicHref = tenantSlug ? `https://${tenantSlug}.pozyskajpacjenta.pl${page.path === '/' ? '' : page.path}` : null;
  return (
    <li className="rounded-[var(--radius-card)] bg-bg p-4 ring-1 ring-black/5">
      <div className="flex items-start gap-3">
        <span className="grid size-10 shrink-0 place-items-center rounded-md bg-surface text-xl ring-1 ring-black/5" aria-hidden="true">
          {arch?.icon ?? '📄'}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <p className="truncate font-medium text-text">{page.title}</p>
            {page.builtin ? (
              <span className="rounded-full bg-surface px-1.5 py-0.5 text-[10px] font-medium text-text-subtle ring-1 ring-border">wbudowana</span>
            ) : null}
          </div>
          <p className="mt-0.5 font-mono text-xs text-text-subtle">{page.path}</p>
          <p className="mt-1 line-clamp-2 text-xs text-text-muted">
            {page.description ?? arch?.description ?? '—'}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            <Button asChild size="sm" variant="secondary">
              <Link to={`/strony/${page.id}` as never}>
                <Edit3 className="size-3.5" aria-hidden="true" /> Edytuj
              </Link>
            </Button>
            {publicHref ? (
              <Button asChild size="sm" variant="ghost">
                <a href={publicHref} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="size-3.5" aria-hidden="true" /> Otwórz
                </a>
              </Button>
            ) : null}
            {onDelete ? (
              <Button type="button" size="icon" variant="ghost" onClick={onDelete} aria-label="Usuń">
                <Trash2 className="size-4" aria-hidden="true" />
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </li>
  );
}

function NewPageDialog({
  existingPaths,
  onCancel,
  onCreate,
}: {
  existingPaths: string[];
  onCancel: () => void;
  onCreate: (page: CustomPage) => void;
}) {
  const [step, setStep] = React.useState<'pick' | 'meta'>('pick');
  const [archetype, setArchetype] = React.useState<PageArchetype | null>(null);
  const [title, setTitle] = React.useState('');
  const [path, setPath] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [pathTouched, setPathTouched] = React.useState(false);

  React.useEffect(() => {
    if (!pathTouched && title) {
      const slug = slugify(title);
      setPath(slug ? `/${slug}` : '');
    }
  }, [title, pathTouched]);

  const pathError = (() => {
    if (!path) return 'Wymagane';
    if (!/^\/[a-z0-9/-]*$/.test(path)) return 'Tylko małe litery, cyfry, "/" i "-"';
    if (existingPaths.includes(path)) return 'Ta ścieżka jest już używana';
    return null;
  })();
  const canSave = !!archetype && title.length >= 3 && !pathError;

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{step === 'pick' ? 'Wybierz szablon strony' : `Nowa strona: ${ARCHETYPES.find((a) => a.id === archetype)?.name}`}</DialogTitle>
          <DialogDescription>
            {step === 'pick'
              ? 'Każdy szablon to gotowa kompozycja sekcji z biblioteki wireframes — wybierz najbliższy potrzebie.'
              : 'Nadaj tytuł, ścieżkę URL i krótki opis. Wszystko można potem edytować w widoku strony.'}
          </DialogDescription>
        </DialogHeader>

        {step === 'pick' ? (
          <ul role="list" className="grid max-h-[60vh] grid-cols-2 gap-2 overflow-y-auto p-1">
            {ARCHETYPES.map((a) => (
              <li key={a.id}>
                <button
                  type="button"
                  onClick={() => { setArchetype(a.id); setStep('meta'); }}
                  className={cn(
                    'flex w-full items-start gap-3 rounded-md bg-surface p-3 text-left ring-1 ring-black/5 transition-all hover:ring-accent/40',
                    archetype === a.id && 'ring-2 ring-accent'
                  )}
                >
                  <span className="grid size-9 shrink-0 place-items-center rounded-md bg-bg text-lg ring-1 ring-black/5" aria-hidden="true">
                    {a.icon}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-text">{a.name}</p>
                    <p className="mt-0.5 line-clamp-2 text-xs text-text-muted">{a.description}</p>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="space-y-4">
            <Field label="Tytuł" htmlFor="np-title" required>
              <Input
                id="np-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="np. Pakiet rocznego badania — promocja"
                maxLength={80}
              />
            </Field>
            <Field label="Ścieżka URL" htmlFor="np-path" required error={pathError ?? undefined}
              hint={`Pełny adres będzie: ${(path && !pathError) ? `…pozyskajpacjenta.pl${path}` : '…pozyskajpacjenta.pl/twoja-strona'}`}>
              <Input
                id="np-path"
                value={path}
                onChange={(e) => { setPath(e.target.value); setPathTouched(true); }}
                placeholder="/pakiet-roczny"
                className="font-mono"
                maxLength={80}
              />
            </Field>
            <Field label="Opis (meta description)" htmlFor="np-desc"
              hint="Wyświetla się w wynikach Google. Min. 80, maks. 160 znaków zalecane.">
              <Textarea
                id="np-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Krótki opis tego, co znajdzie pacjent na tej stronie."
                maxLength={200}
                rows={3}
              />
            </Field>
          </div>
        )}

        <DialogFooter className="gap-2">
          {step === 'meta' ? (
            <Button type="button" variant="ghost" onClick={() => setStep('pick')}>
              ← Zmień szablon
            </Button>
          ) : null}
          <Button type="button" variant="ghost" onClick={onCancel}>
            Anuluj
          </Button>
          {step === 'meta' ? (
            <Button
              type="button"
              disabled={!canSave}
              onClick={() => {
                if (!archetype || !canSave) return;
                onCreate({
                  id: `cp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
                  archetype,
                  title: title.trim(),
                  path,
                  description: description.trim() || undefined,
                  created_at: Math.floor(Date.now() / 1000),
                  updated_at: Math.floor(Date.now() / 1000),
                });
              }}
            >
              <FileText className="size-4" aria-hidden="true" /> Utwórz stronę
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
